import { DEVICE_ID_PATTERN } from '../model/constants';
import { formatHlc, HLC_MAX_COUNTER, HLC_MAX_MS, isHlcBeyondDrift, parseHlc } from './hlc';

export interface HlcClock {
  readonly deviceId: string;
  /** A new timestamp, strictly greater than every earlier `tick` and every `receive`d one. */
  tick(): string;
  /**
   * Advance the clock past a timestamp seen from elsewhere (another device, local storage).
   * A timestamp more than `MAX_HLC_DRIFT_MS` ahead of the wall clock is ignored.
   */
  receive(remote: string): void;
}

/**
 * Hybrid logical clock for one device. `now` supplies the wall clock in epoch milliseconds.
 * Timestamps stay monotonic when the wall clock stands still or goes backwards (the counter
 * increments); when the 16-bit counter would overflow the millisecond part moves one ahead.
 * `tick` never throws: at the largest representable time it repeats the largest timestamp.
 */
export function createHlcClock(deviceId: string, now: () => number): HlcClock {
  if (!DEVICE_ID_PATTERN.test(deviceId)) throw new RangeError(`Invalid device id: ${deviceId}`);
  let lastMs = 0;
  let lastCounter = 0;

  /** The wall clock as a whole, representable number of milliseconds. */
  function wallMs(): number {
    const wall = Math.floor(now());
    return Number.isFinite(wall) ? Math.min(Math.max(wall, 0), HLC_MAX_MS) : 0;
  }

  return {
    deviceId,
    tick(): string {
      const wall = wallMs();
      if (wall > lastMs) {
        lastMs = wall;
        lastCounter = 0;
      } else if (lastCounter < HLC_MAX_COUNTER) {
        lastCounter += 1;
      } else if (lastMs < HLC_MAX_MS) {
        lastMs += 1;
        lastCounter = 0;
      }
      return formatHlc({ ms: lastMs, counter: lastCounter, deviceId });
    },
    receive(remote: string): void {
      const parsed = parseHlc(remote);
      if (isHlcBeyondDrift(remote, wallMs())) return;
      if (parsed.ms > lastMs || (parsed.ms === lastMs && parsed.counter > lastCounter)) {
        lastMs = parsed.ms;
        lastCounter = parsed.counter;
      }
    },
  };
}
